import { Placement, DeviceFamily, MarketPlace } from '@amzn/fire-tv-campaign-automation-service';
import { useMemo } from 'react';
import { logger } from 'src/logger';

interface EmbeddedModeState {
  isEmbedded: boolean;
  embeddedPlacement: Placement | null;
  embeddedDeviceFamily: DeviceFamily | null;
  embeddedMarketplace: MarketPlace | null;
}

const NON_EMBEDDED: EmbeddedModeState = {
  isEmbedded: false,
  embeddedPlacement: null,
  embeddedDeviceFamily: null,
  embeddedMarketplace: null,
};

const validateEnum = <T extends Record<string, string>>(
  value: string | null,
  enumObj: T,
  name: string,
): T[keyof T] | null => {
  if (!value) return null;
  const enumValues = Object.values(enumObj) as string[];
  if (enumValues.includes(value)) return value as T[keyof T];
  const sanitized = value.replace(/[\n\r]/g, '').slice(0, 100);
  logger.warn('useEmbeddedMode: Invalid enum param', { name, value: sanitized });
  return null;
};

// URL params are parsed once — they don't change during SPA lifecycle
export const useEmbeddedMode = (): EmbeddedModeState => {
  return useMemo(() => {
    if (typeof window === 'undefined') return NON_EMBEDDED;
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') !== 'embedded') return NON_EMBEDDED;
    return {
      isEmbedded: true,
      embeddedPlacement: validateEnum(params.get('placement'), Placement, 'placement'),
      embeddedDeviceFamily: validateEnum(params.get('deviceFamily'), DeviceFamily, 'deviceFamily'),
      embeddedMarketplace: validateEnum(params.get('marketplace'), MarketPlace, 'marketplace'),
    };
  }, []);
};
